import { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import RunningExecutionView from "./RunningExecutionView";
import CompletedExecutionView from "./CompletedExecutionView";
import SessionIdCopyButton from "../../../../common/SessionIdCopyButton";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import AutomationText from "../../shared/AutomationText";
import { formatDuration } from '../../../../AutomationStudio/utils';
import { computeExecutionSummaryStats, hasExecutionResultsUI } from '../../../../AutomationStudio/executionSummaryStats';
import { getReplayStepDisplayDescription } from '../../../../AutomationStudio/utils/stepExecutionDisplay';
import { resolveExecutionBannerDisplay } from '../../../../../features/automationStudioNew/executionOutcomeUtils';

/**
 * ExecutionView - Monitors script execution in real-time
 * Two sub-states: RUNNING (live progress) and COMPLETED (results)
 */
export default function ExecutionView({
    isExecuting = false,
    executionResults = null,
    replayExecutionResults = null,
    replayProgressMap = {},
    replayExecuting = false,
    replayCurrentStepIndex = null,
    flatStepsForReplay = [],
    overallStatus = null,
    instructions = [],
    screenshots = [],
    logs = [],
    executionFlow = [],
    testCompleted = false,
    automationError = null,
    onStepClick = null,
    onRetry = null,
    onStopExecution = null,
    onSaveExecutionRecord = null,
    isSavedExecutionRecord = false,
    canSaveExecutionRecord = false,
    onRunAgain = null,
    onEditInstructions = null,
    onStepSelect = null,
    sessionId = null,
    showDynamicScriptChip = false,
    isReplayFlow = false,
    hasExecutedInSession = false,
    isGenerating = false,
}) {
    const [currentStep, setCurrentStep] = useState(null);
    const [executionLogs, setExecutionLogs] = useState([]);
    const [allScreenshots, setAllScreenshots] = useState([]);

    const executionState = isExecuting ? 'RUNNING' : 'COMPLETED';

    useEffect(() => {
        if (!isExecuting) {
            setCurrentStep(null);
            return;
        }
        if (replayExecuting && replayCurrentStepIndex != null && flatStepsForReplay?.length > 0) {
            const flatStep = flatStepsForReplay[replayCurrentStepIndex];
            if (flatStep?.id) {
                for (const inst of instructions || []) {
                    const step = (inst.aiSteps || []).find(s => s.id === flatStep.id);
                    if (step) {
                        setCurrentStep(step);
                        return;
                    }
                }
            }
            setCurrentStep(null);
            return;
        }
        if (executionResults?.currentStepId) {
            for (const inst of instructions || []) {
                const step = (inst.aiSteps || []).find(s => s.id === executionResults.currentStepId);
                if (step) {
                    setCurrentStep(step);
                    return;
                }
            }
        }
        setCurrentStep(null);
    }, [isExecuting, replayExecuting, replayCurrentStepIndex, flatStepsForReplay, executionResults, instructions]);

    useEffect(() => {
        if (executionResults?.logs) {
            setExecutionLogs(executionResults.logs);
        } else if (logs) {
            setExecutionLogs(logs);
        }
    }, [executionResults, logs]);

    useEffect(() => {
        const hasReplayProgress = replayProgressMap && Object.keys(replayProgressMap).length > 0;
        const hasReplayStepDetails =
            replayExecutionResults?.stepDetails &&
            Object.keys(replayExecutionResults.stepDetails || {}).length > 0;

        if (hasReplayProgress && instructions?.length > 0) {
            const collected = [];
            let flatIdx = 0;

            instructions.forEach(inst => {
                (inst.aiSteps || []).forEach(step => {
                    const stepIndexForLookup =
                        flatStepsForReplay.find((s) => s?.id === step.id)?.stepIndex ?? flatIdx;

                    const statusFromReplay = replayProgressMap[stepIndexForLookup];
                    const isExecutedReplay = ["Passed", "Failed"].includes(statusFromReplay);

                    if (!isExecutedReplay) {
                        flatIdx++;
                        return;
                    }

                    const detail = hasReplayStepDetails ? replayExecutionResults.stepDetails?.[stepIndexForLookup] : null;

                    const path = hasReplayStepDetails
                        ? (
                            detail?.screenshot
                            ?? detail?.result?.screenshot
                            ?? detail?.result?.result?.screenshot
                        )
                        : (
                            detail?.screenshot
                            ?? detail?.result?.screenshot
                            ?? detail?.result?.result?.screenshot
                            ?? step.screenshot
                            ?? step.executionGroup?.mainAction?.details?.screenshotPath
                        );

                    if (path) {
                        const rawPath = typeof path === 'string' ? path : path?.url || path?.path;
                        collected.push({
                            stepId: step.id,
                            stepDescription: getReplayStepDisplayDescription(step, detail),
                            path: rawPath,
                            url: rawPath,
                            timestamp: step.executedAt || step.timestamp || detail?.result?.timestamp,
                            status: statusFromReplay === 'Passed'
                                ? 'success'
                                : 'failed'
                        });
                    }

                    flatIdx++;
                });
            });

            setAllScreenshots(collected);
            return;
        }

        if (screenshots && screenshots.length > 0) {
            setAllScreenshots(screenshots);
        } else {
            setAllScreenshots([]);
        }
    }, [instructions, screenshots, replayExecutionResults, replayProgressMap, flatStepsForReplay]);

    const bannerDisplay = resolveExecutionBannerDisplay({
        overallStatus,
        automationError,
        isExecuting,
        replayExecuting,
    });
    const { message, bannerVariant, showMessage } = bannerDisplay;

    const executionStats = computeExecutionSummaryStats({
        instructions,
        replayProgressMap,
        replayExecutionResults,
        executionResults,
        overallStatus,
        automationError,
    });

    const showResultsShell = hasExecutionResultsUI({
        testCompleted,
        instructions,
        replayProgressMap,
        automationError,
        replayExecutionResults,
    });

    const bannerTitle =
        bannerVariant === 'passed'
            ? LABELS.EXECUTION_SUCCESS
            : bannerVariant === 'partial'
              ? LABELS.EXECUTION_PARTIAL
              : LABELS.EXECUTION_FAILED;

    const showSummaryBanner = !isExecuting && !replayExecuting && showResultsShell;

    const statsPills = (
        <div className={styles.executionSummaryStatsPills}>
            <div className={styles.statCard}>
                <span className={`${styles.statLabel} ${styles.asTypoCaption}`}>{LABELS.TOTAL_STEPS}</span>
                <span className={styles.statValue}>{executionStats.totalSteps}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statCardSuccess}`}>
                <span className={`${styles.statLabel} ${styles.asTypoCaption}`}>{LABELS.SUCCESSFUL}</span>
                <span className={styles.statValue}>{executionStats.successSteps}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statCardFailed}`}>
                <span className={`${styles.statLabel} ${styles.asTypoCaption}`}>{LABELS.FAILED_LABEL}</span>
                <span className={styles.statValue}>{executionStats.failedSteps}</span>
            </div>
            {executionStats.skippedSteps > 0 && (
                <div className={styles.statCard}>
                    <span className={`${styles.statLabel} ${styles.asTypoCaption}`}>{LABELS.SKIPPED_LABEL}</span>
                    <span className={styles.statValue}>{executionStats.skippedSteps}</span>
                </div>
            )}
            <div className={styles.statCard}>
                <span className={`${styles.statLabel} ${styles.asTypoCaption}`}>Duration</span>
                <span className={styles.statValue}>{formatDuration(executionStats.totalDuration)}</span>
            </div>
        </div>
    );

    return (
        <div className={styles.executionViewContainer}>
            {sessionId && (
                <div className={styles.sessionIdBar}>
                    <SessionIdCopyButton sessionId={sessionId} label="Session ID" labelClassName={styles.sessionIdBarLabel} />
                    <div className={styles.sessionIdBarRight}>
                        {showDynamicScriptChip ? (
                            <span className={styles.quickStatsDynamicChip} role="status" aria-label="Dynamic enrichment enabled for this plan">
                                Dynamic
                            </span>
                        ) : null}
                    </div>
                </div>
            )}
            {showSummaryBanner && (
                showMessage ? (
                    <div
                        className={
                            bannerVariant === 'passed'
                                ? styles.successSection
                                : bannerVariant === 'partial'
                                  ? styles.partialSection
                                  : styles.errorSection
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <div className={styles.executionBannerLayout}>
                            <div className={styles.executionSummaryRow}>
                                <p
                                    className={
                                        bannerVariant === 'passed'
                                            ? styles.successLabel
                                            : bannerVariant === 'partial'
                                              ? styles.partialLabel
                                              : styles.errorLabel
                                    }
                                >
                                    {bannerTitle}
                                </p>
                                <div className={styles.executionSummaryStatsWrap}>{statsPills}</div>
                            </div>
                            <div
                                className={`${
                                    bannerVariant === 'passed'
                                        ? styles.successMessageContainer
                                        : bannerVariant === 'partial'
                                          ? styles.partialMessageContainer
                                          : styles.errorMessageContainer
                                } ${styles.executionBannerMessageBody}`}
                                aria-live="polite"
                            >
                                <Tooltip
                                    title={<AutomationText text={message} />}
                                    arrow
                                    placement="top"
                                    enterDelay={300}
                                >
                                    <span
                                        className={`${styles.tooltipHost} ${
                                            bannerVariant === 'passed'
                                                ? styles.successMessageText
                                                : bannerVariant === 'partial'
                                                  ? styles.partialMessageText
                                                  : styles.errorMessageText
                                        }`}
                                    >
                                        <AutomationText text={message} />
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.executionStatsStandalone} style={{ marginBottom: 16 }}>
                        {statsPills}
                    </div>
                )
            )}
            {executionState === 'RUNNING' ? (
                <RunningExecutionView
                    currentStep={currentStep}
                    instructions={instructions}
                    executionResults={executionResults}
                    replayExecutionResults={replayExecutionResults}
                    replayProgressMap={replayProgressMap}
                    replayExecuting={replayExecuting}
                    replayCurrentStepIndex={replayCurrentStepIndex}
                    flatStepsForReplay={flatStepsForReplay}
                    onStopExecution={onStopExecution}
                    onSaveExecutionRecord={onSaveExecutionRecord}
                    isSavedExecutionRecord={isSavedExecutionRecord}
                    canSaveExecutionRecord={canSaveExecutionRecord}
                />
            ) : (
                <CompletedExecutionView
                    executionResults={executionResults}
                    overallStatus={overallStatus}
                    instructions={instructions}
                    replayProgressMap={replayProgressMap}
                    replayExecutionResults={replayExecutionResults}
                    flatStepsForReplay={flatStepsForReplay}
                    executionLogs={executionLogs}
                    screenshots={allScreenshots}
                    executionFlow={executionFlow}
                    automationError={automationError}
                    testCompleted={testCompleted}
                    isReplayFlow={isReplayFlow}
                    hasExecutedInSession={hasExecutedInSession}
                    isRunning={isExecuting}
                    isReplayExecuting={replayExecuting}
                    isGenerating={isGenerating}
                    onStepClick={onStepClick || onStepSelect}
                    onRetry={onRetry || onRunAgain}
                    onEditInstructions={onEditInstructions}
                />
            )}
        </div>
    );
}
